import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './SmartImage.scss';

import { getSmartImage } from '../../Helpers/helpers';

const SmartImage = props => {
  /*
    Optimal lazy loading for images and basic animation effects.

    Example:
    <SmartImage
      className="example-image" // Add your custom CSS class
      src={imageUrl} // URL of the image to display
      alt="Description of image" // Accessibility text for screen readers
      growIn={true} // Enables grow animation effect - use for emphasis
      noFadeIn={true} // Disable default fade-in effect
      fadeInDelay={100} // Milliseconds to delay fade-in - useful for staggered loading
      lazy={true} // Only loads when scrolled into view - use for performance on long pages
      lazyRootMargin="100%" // How far from viewport to start loading - increase for earlier loading
      fallbackText="Image not available" // Text to show if image fails to load
      overrideTransition={false} // Set true to disable default transitions and use your own
    />
  */

  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(!props.lazy);
  const imgRef = useRef();
  const rootMargin = props.lazyRootMargin || '100%';

  // Smart image component with lazy loading, fade-in effects, and error handling
  useEffect(() => {
    if (!props.lazy) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [props.lazy, rootMargin]);

  const onLoad = () => {
    setTimeout(() => setIsLoaded(true), props.fadeInDelay || 10);
  };

  const onError = () => setIsError(true);

  const propsToPassThrough = _.omit(props, ['fadeInDelay', 'fallbackText', 'growIn', 'lazy', 'lazyRootMargin', 'noFadeIn', 'overrideTransition']);

  // Handle fallback text if image fails to load
  if (isError) {
    return <div className='smart-image-fallback'>{props.fallbackText}</div>;
  }

  const additionalClasses = {
    'override-transition': props.overrideTransition,
    'grow-in': props.growIn || false,
    'no-fade-in': props.noFadeIn || false,
    loading: !isLoaded,
    loaded: isLoaded
  };

  return (
    <img
      ref={imgRef}
      {...propsToPassThrough}
      className={cn('smart-image', additionalClasses, props.className)}
      src={shouldLoad ? getSmartImage(props.src, false) : undefined}
      alt={props.alt || 'Smart Img'}
      onLoad={onLoad}
      onError={onError}
      loading={props.lazy ? 'lazy' : undefined}
    />
  );
};

SmartImage.propTypes = {
  // General Image Props
  className: PropTypes.string,
  src: PropTypes.string,
  alt: PropTypes.string,
  fallbackText: PropTypes.string,

  // Animation props
  growIn: PropTypes.bool, // Grow the image in
  noFadeIn: PropTypes.bool, // Disable default fade-in effect
  fadeInDelay: PropTypes.number, // Milliseconds to delay fade-in - useful for staggered loading
  overrideTransition: PropTypes.bool, // Set true to disable default transitions and use your own

  // Lazy loading props
  lazy: PropTypes.bool, // Only loads when scrolled into view - use for performance on long pages
  lazyRootMargin: PropTypes.string // When the image should load if you use "lazy", default: '100%'
};

export default SmartImage;
